import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { FiCheck, FiCopy, FiUser } from "react-icons/fi";
import {
  createPayoutIdempotencyKey,
  isPayoutApiMode,
  payoutApi,
} from "./api/payoutApiClient";
import {
  errorMessage,
  pickString,
  readSessionJson,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import { normalizeApiStatus } from "./api/payoutApiAdapters";
import PayoutCancelScheduledTransferModal from "./PayoutCancelScheduledTransferModal";

const formatINR = (amount: number) =>
  `\u20B9 ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatCountdown = (seconds: number) => {
  const safe = Math.max(0, seconds);
  const mm = String(Math.floor(safe / 60)).padStart(2, "0");
  const ss = String(safe % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

type TransferDraft = {
  account: {
    title: string;
    description?: string;
  };
  beneficiary: {
    title: string;
    description?: string;
  };
  method: string;
  scheduledFor: string;
  amount: number;
};

const defaultDraft: TransferDraft = {
  account: {
    title: "-",
    description: "-",
  },
  beneficiary: {
    title: "-",
    description: "-",
  },
  method: "-",
  scheduledFor: "-",
  amount: 0,
};

const readTransferDraft = () => {
  try {
    const storedDraft = sessionStorage.getItem("payoutTransferDraft");
    return storedDraft ? (JSON.parse(storedDraft) as TransferDraft) : defaultDraft;
  } catch {
    return defaultDraft;
  }
};

function FlowStep({
  label,
  complete,
}: {
  label: string;
  complete?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={
          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold " +
          (complete
            ? "bg-[#1B163B] text-white"
            : "border border-[#D6DCEB] bg-white text-[#505E8E]")
        }
      >
        {complete ? <FiCheck /> : null}
      </span>
      <span className="text-sm font-semibold text-[#505E8E]">{label}</span>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] px-5 py-4 md:grid-cols-[190px_minmax(0,1fr)]">
      <span className="text-sm font-semibold text-[#1B163B]">{label}</span>
      <div className="text-sm font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

function WorkflowBox() {
  const steps = [
    ["M", "Maker", "-", "Completed"],
    ["C", "Checker", "-", "Pending"],
    ["A", "Approver", "-", "Pending"],
  ];

  return (
    <div className="rounded-[8px] border border-[#BBD1FF] bg-[#F8FAFF] p-5">
      <h3 className="mb-5 text-sm font-semibold text-[#1B163B]">
        Approval Workflow
      </h3>
      <div className="flex flex-wrap items-center justify-around gap-5">
        {steps.map(([stage, title, helper, status], index) => (
          <React.Fragment key={stage}>
            <div className="text-center">
              <span
                className={
                  "mx-auto flex h-11 w-11 items-center justify-center rounded-full text-lg " +
                  (stage === "M"
                    ? "bg-[#DCFCE7] text-[#16A34A]"
                    : stage === "C"
                      ? "bg-[#FEF3C7] text-[#F59E0B]"
                      : "bg-[#EDE9FE] text-[#6D28D9]")
                }
              >
                {stage === "M" ? <FiCheck /> : <FiUser />}
              </span>
              <p className="mt-3 text-sm font-semibold text-[#1B163B]">
                {title}
              </p>
              <p className="mt-1 text-xs font-medium text-[#505E8E]">
                {helper}
              </p>
              <p
                className={
                  "mt-2 text-xs font-semibold " +
                  (status === "Completed" ? "text-[#16A34A]" : "text-[#F59E0B]")
                }
              >
                {status}
              </p>
            </div>
            {index < steps.length - 1 ? (
              <span className="hidden h-px w-10 bg-[#D6DCEB] md:block" />
            ) : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function PayoutConfirmTransfer() {
  const draft = useMemo(readTransferDraft, []);
  const initialResult = useMemo(
    () => readSessionJson<Record<string, unknown> | null>("payoutTransferCreateResult", null),
    [],
  );
  const [createResult, setCreateResult] = useState(initialResult);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [cancelMessage, setCancelMessage] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const paymentId = pickString(createResult, [
    "payment_id",
    "paymentId",
    "id",
  ]);
  const clientReferenceId = pickString(createResult, [
    "client_reference_id",
    "clientReferenceId",
  ]);
  const paymentStatus = normalizeApiStatus(
    pickString(createResult, ["status", "status_label", "statusLabel"], "Pending"),
  );
  const utr = pickString(createResult, ["utr", "utr_number", "utrNumber"]);
  const executeAt = pickString(createResult, ["execute_at", "executeAt"]);
  const isScheduled = paymentStatus === "Scheduled";

  useEffect(() => {
    if (!isScheduled || !createResult) return;
    const fromApi =
      typeof createResult.scheduled_remaining_seconds === "number"
        ? createResult.scheduled_remaining_seconds
        : typeof createResult.scheduledRemainingSeconds === "number"
          ? createResult.scheduledRemainingSeconds
          : null;
    if (typeof fromApi === "number") {
      setRemainingSeconds(fromApi);
      return;
    }
    if (executeAt) {
      const ms = new Date(executeAt).getTime() - Date.now();
      setRemainingSeconds(Math.max(0, Math.ceil(ms / 1000)));
    }
  }, [createResult, executeAt, isScheduled]);

  useEffect(() => {
    if (!isScheduled || remainingSeconds === null) return;
    if (remainingSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setRemainingSeconds((current) =>
        current === null ? null : Math.max(0, current - 1),
      );
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isScheduled, remainingSeconds]);

  useEffect(() => {
    if (!isPayoutApiMode() || !paymentId || !isScheduled) return;
    const poll = window.setInterval(async () => {
      try {
        const latest = await payoutApi.paymentDetails(paymentId);
        writeSessionJson("payoutTransferCreateResult", latest);
        setCreateResult(latest);
        const status = normalizeApiStatus(
          pickString(latest, ["status"], ""),
        );
        if (status !== "Scheduled") {
          setRemainingSeconds(0);
        } else if (typeof latest.scheduled_remaining_seconds === "number") {
          setRemainingSeconds(latest.scheduled_remaining_seconds);
        }
      } catch {
        // keep local countdown
      }
    }, 10_000);
    return () => window.clearInterval(poll);
  }, [isScheduled, paymentId]);

  const handleCancel = async () => {
    if (!paymentId || cancelling) return;
    setCancelling(true);
    setCancelMessage("");
    try {
      const cancelled = await payoutApi.cancelPayment(
        paymentId,
        { reason: "Cancelled during cooling-off window" },
        createPayoutIdempotencyKey("cancel-scheduled", paymentId),
      );
      writeSessionJson("payoutTransferCreateResult", cancelled);
      setCreateResult(cancelled);
      setCancelMessage("Transfer cancelled before bank submission.");
      setRemainingSeconds(null);
      setCancelModalOpen(false);
    } catch (error) {
      setCancelMessage(errorMessage(error, "Unable to cancel this transfer."));
    } finally {
      setCancelling(false);
    }
  };

  if (isPayoutApiMode() && !createResult) {
    return (
      <Navigate
        to="/payouts/transfers/initiate"
        replace
        state={{
          error:
            "No payment was created. Complete verification on the previous step — a red error there means the API rejected the transfer.",
        }}
      />
    );
  }

  const transferDate =
    isScheduled && executeAt
      ? new Date(executeAt).toLocaleString("en-IN")
      : draft.scheduledFor && draft.scheduledFor !== "Now"
        ? draft.scheduledFor
        : "Immediate";

  const headline = isScheduled
    ? "Transfer Scheduled"
    : paymentStatus === "Cancelled"
      ? "Transfer Cancelled"
      : "Transfer Initiated Successfully!";

  const helper = isScheduled
    ? `Bank submission starts in ${formatCountdown(remainingSeconds ?? 0)}. You can cancel until then.`
    : isPayoutApiMode()
      ? `Payment ${paymentId || clientReferenceId} was submitted to payouts-service.`
      : "Your transfer request has been created and is now pending approval.";

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Confirm Transfer
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Transfer request has been created successfully.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-10">
        <FlowStep label="Enter Details" complete />
        <span className="h-px w-20 bg-[#D6DCEB]" />
        <FlowStep label="Review" complete />
        <span className="h-px w-20 bg-[#D6DCEB]" />
        <FlowStep label="Verify" complete />
        <span className="h-px w-20 bg-[#D6DCEB]" />
        <FlowStep label="Confirm" complete />
      </div>

      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 text-center new-shadow">
        <div
          className={
            "mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full text-4xl " +
            (paymentStatus === "Cancelled"
              ? "bg-[#FEE2E2] text-[#EF4444]"
              : isScheduled
                ? "bg-[#EDE9FE] text-[#6D28D9]"
                : "bg-[#DCFCE7] text-[#16A34A]")
          }
        >
          <FiCheck />
        </div>
        <h2 className="text-[22px] font-semibold text-[#1B163B]">{headline}</h2>
        <p className="mt-3 text-sm font-medium text-[#505E8E]">{helper}</p>
        {isScheduled && remainingSeconds !== null ? (
          <p className="mt-4 text-3xl font-semibold text-[#6D28D9]">
            {formatCountdown(remainingSeconds)}
          </p>
        ) : null}
        {cancelMessage ? (
          <p className="mt-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
            {cancelMessage}
          </p>
        ) : null}

        <div className="mt-7 overflow-hidden rounded-[8px] border border-[#E0E5F2] text-left">
          <DetailRow
            label="Reference ID"
            value={
              <span className="flex items-center justify-between gap-3 break-all">
                {paymentId || clientReferenceId || "-"}
                <FiCopy className="shrink-0 text-[#505E8E]" />
              </span>
            }
          />
          {utr ? <DetailRow label="UTR" value={utr} /> : null}
          <DetailRow
            label="Source Account"
            value={
              <span>
                {draft.account?.title ?? "—"}
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  {draft.account?.description || "-"}
                </span>
              </span>
            }
          />
          <DetailRow
            label="Beneficiary"
            value={
              <span>
                {draft.beneficiary.title || "-"}
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  {draft.beneficiary.description || "-"}
                </span>
              </span>
            }
          />
          <DetailRow label="Amount" value={formatINR(draft.amount)} />
          <DetailRow label="Transfer Type" value={draft.method} />
          <DetailRow label="Transfer Date" value={transferDate} />
          <DetailRow
            label="Status"
            value={
              <span
                className={
                  "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                  (paymentStatus === "Scheduled"
                    ? "bg-[#EDE9FE] text-[#6D28D9]"
                    : paymentStatus === "Cancelled"
                      ? "bg-[#FEE2E2] text-[#EF4444]"
                      : "bg-[#FEF3C7] text-[#F59E0B]")
                }
              >
                {paymentStatus}
              </span>
            }
          />
        </div>

        {isScheduled ? (
          <div className="mt-6">
            <button
              type="button"
              disabled={cancelling}
              onClick={() => setCancelModalOpen(true)}
              className="h-10 rounded-[6px] border border-[#FCA5A5] bg-white px-7 text-sm font-semibold text-[#DC2626] disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel Transfer"}
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 text-left">
              <WorkflowBox />
            </div>
            <div className="mt-6 rounded-[8px] border border-[#E0E5F2] bg-white p-5 text-left">
              <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
                What's Next?
              </h3>
              <p className="mb-3 text-sm font-medium text-[#505E8E]">
                {"\u2022"} You can view and track this request in{" "}
                <Link
                  to="/payouts/transfers"
                  className="font-semibold text-[#1B163B]"
                >
                  Transfers
                </Link>
                .
              </p>
              <p className="text-sm font-medium text-[#505E8E]">
                {"\u2022"} You will receive notifications on status updates.
              </p>
            </div>
          </>
        )}
      </section>

      <div className="mt-8 flex max-w-[760px] items-center justify-between rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <Link
          to="/payouts/transfers"
          className="flex h-10 items-center rounded-[6px] border border-[#1B163B] px-7 text-sm font-semibold text-[#1B163B]"
        >
          View Transfers
        </Link>
        <Link
          to="/payouts/transfers/initiate"
          className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
        >
          Initiate Another Transfer
        </Link>
      </div>

      {cancelModalOpen ? (
        <PayoutCancelScheduledTransferModal
          transactionId={paymentId || clientReferenceId || "-"}
          beneficiary={draft.beneficiary.title}
          amount={formatINR(draft.amount)}
          scheduledFor={transferDate}
          confirming={cancelling}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={() => void handleCancel()}
        />
      ) : null}
    </div>
  );
}

export default PayoutConfirmTransfer;
